import React from "react";
import { PollCommonProps } from "../poll";
import { usePollContext } from "../../stores/poll";
import { usePollResults, useVoteWithAnalytics } from "../poll";
import { Poll, PollSkeleton } from "../poll";

import styles from "./styles.modules.scss";

export const PollBlock = ({
    pollId,
    options,
    pollIndex,
    campaignId,
    ...viewProps
}: PollCommonProps & { pollIndex: number; campaignId: string | undefined }) => {
    const {
        state: { voteCache, votingState, polls },
        canVote,
        isClosed,
        actions: { vote, refreshPollInfo },
    } = usePollContext();
    const { optionsWithResults, showResults } = usePollResults(pollId, options);
    const voteInPoll = (optionId: string[]) => vote(pollId, optionId);
    const voteWithAnalytics = useVoteWithAnalytics(pollId, options, voteInPoll);
    const ref = React.useRef<HTMLDivElement>(null);
    const previousScrollTimeRef = React.useRef(0);

    React.useEffect(() => {
        refreshPollInfo(pollId);
    }, [pollId]);

    React.useEffect(() => {
        if (campaignId === "world") {
            return;
        }

        const SNAP_TO_SCROLL_TIMING = 300;

        const onScroll = () => {
            const pollPos = {
                top: ref.current?.getBoundingClientRect().top ?? 0,
                bottom: ref.current?.getBoundingClientRect().bottom ?? 0,
            };
            const windowHeight = window.innerHeight;
            const snapPointTop = windowHeight * 0.33;
            const snapPointBottom = windowHeight * 0.66;

            // Using ref here as updated state value is not immediately available for the defined listener
            previousScrollTimeRef.current = new Date().getTime();

            setTimeout(() => {
                if (
                    new Date().getTime() - previousScrollTimeRef.current <=
                    SNAP_TO_SCROLL_TIMING - 100
                )
                    return;

                if (
                    pollPos.top < snapPointTop &&
                    pollPos.bottom > snapPointBottom
                ) {
                    ref.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            }, SNAP_TO_SCROLL_TIMING);
        };

        window.addEventListener("scroll", onScroll, true);

        return () => {
            window.removeEventListener("scroll", onScroll, true);
        };
    }, [campaignId]);

    const uniqueClassName = `poll-${pollIndex}`;

    return polls[pollId] ? (
        <Poll
            extraClassNames={[
                String(styles.block),
                "block-poll",
                uniqueClassName,
            ]}
            isClosed={isClosed(pollId)}
            userVote={voteCache.votes[pollId] ?? null}
            canVote={canVote(pollId) && polls[pollId]?.status !== "closed"}
            vote={voteWithAnalytics}
            options={optionsWithResults}
            showResults={showResults}
            isError={votingState === "fail"}
            isVoting={votingState === "voting"}
            pollRef={ref}
            campaignId={campaignId}
            {...viewProps}
        />
    ) : (
        <PollSkeleton
            extraClassNames={[
                String(styles.block),
                "block-poll",
                uniqueClassName,
            ]}
            {...viewProps}
        />
    );
};
